/**
 * Scene Component
 * Main React Three Fiber Canvas mit Postprocessing
 * Alle Skulpturen vertikal gestapelt, Kamera animiert
 */

import { useState, useEffect, useCallback, memo } from 'react'
import { Canvas } from '@react-three/fiber'
import { EffectComposer, Bloom } from '@react-three/postprocessing'
import { CameraController } from './CameraController'
// GlowShape no longer used here — the museum redesign drops the
// neon polygons behind the sculptures.
import { Lighting } from './Lighting'
import { SculptureModel, BBoxInfo } from './SculptureModel'
import { GlowSettings, DARK_MODE_SETTINGS } from '@/components/ui/DevSettings'
import { Artwork } from '@/types/artwork'
import { ViewMode } from '@/types/three'

// Abstand zwischen Skulpturen (vertikal)
const SCULPTURE_SPACING = 7

/**
 * Memoised wrapper that gives each SculptureModel a stable
 * onBBoxCalculated callback. Without this, the inline `(bbox) => ...`
 * arrow created in Scene's map was a fresh function reference every
 * render — which kicked GLBModel's `useEffect([debugBox, onBBoxCalculated])`
 * into an infinite loop (the stack trace bottomed out in a postMessage
 * cascade between SculptureModel.tsx:119 and Scene.tsx:74/168/170).
 * Each loop iteration allocated a new Vector3 + bbox object, growing
 * the heap by ~200 KB per frame at 60 fps.
 */
const SculptureMount = memo(function SculptureMount({
  artwork,
  posY,
  onBBoxCalculated,
}: {
  artwork: Artwork
  posY: number
  onBBoxCalculated: (id: string, bbox: BBoxInfo) => void
}) {
  // Bind the id once per artwork so the callback identity is stable for
  // the lifetime of this SculptureMount.
  const idCallback = useCallback(
    (bbox: BBoxInfo) => onBBoxCalculated(artwork.id, bbox),
    [artwork.id, onBBoxCalculated],
  )
  return (
    <group position={[0, posY, 0]}>
      <SculptureModel artwork={artwork} onBBoxCalculated={idCallback} />
    </group>
  )
})

interface SceneProps {
  artworks: Artwork[]
  currentIndex: number
  viewMode: ViewMode
  onTransitionStart?: () => void
  onTransitionComplete?: () => void
  glowSettings?: GlowSettings
}

export function Scene({ artworks, currentIndex, viewMode, onTransitionStart, onTransitionComplete, glowSettings }: SceneProps) {
  // Canvas runs transparent so the body's radial gradient shows
  // through — no need for a solid canvasBg here.

  // Track bounding boxes for each sculpture
  const [bboxMap, setBboxMap] = useState<Map<string, BBoxInfo>>(new Map())

  // Pause animation loop when the tab is hidden. With six sculptures
  // each running auto-rotation + an EffectComposer with mipmap-blur
  // Bloom at 60fps, a backgrounded tab kept burning GPU memory until
  // Chrome killed the renderer. `frameloop='never'` stops rAF entirely;
  // visibility 'visible' switches back to 'always'.
  const [frameloop, setFrameloop] = useState<'always' | 'never'>('always')
  useEffect(() => {
    const onVisChange = () => {
      setFrameloop(document.hidden ? 'never' : 'always')
    }
    document.addEventListener('visibilitychange', onVisChange)
    return () => document.removeEventListener('visibilitychange', onVisChange)
  }, [])

  // Callback when a sculpture reports its bbox
  const handleBBoxCalculated = useCallback((artworkId: string, bbox: BBoxInfo) => {
    setBboxMap(prev => {
      const next = new Map(prev)
      next.set(artworkId, bbox)
      return next
    })
  }, [])

  // Get current sculpture's bbox
  const currentArtwork = artworks[currentIndex]
  const currentBBox = currentArtwork ? bboxMap.get(currentArtwork.id) : undefined

  // Use provided settings or dark defaults (light mode removed).
  const settings = glowSettings || DARK_MODE_SETTINGS

  // Kamera Y-Position basierend auf aktuellem Index
  const cameraY = -currentIndex * SCULPTURE_SPACING

  return (
    <Canvas
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'transparent',
      }}
      shadows
      // Cap device-pixel-ratio at 2. Retina displays default to 3, which
      // means every resize event allocates render targets at 9× the
      // raster area of a 1× backbuffer. On rapid drag-resize we saw
      // Chrome kill the tab with SBOX_FATAL_MEMORY_EXCEEDED — clamping
      // dpr keeps the GPU footprint bounded.
      dpr={[1, 2]}
      // R3F's default resize observer fires every animation frame
      // during a window drag. `debounce.resize` defers the actual
      // setSize() call until the user stops dragging, so the
      // EffectComposer + Bloom render targets only reallocate once at
      // the final size.
      resize={{ debounce: 200, scroll: false, offsetSize: false }}
      frameloop={frameloop}
      gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
      onCreated={({ gl, scene }) => {
        // Expose the renderer for in-browser diagnostics so we can poll
        // gl.info.memory.{geometries,textures} from devtools when
        // hunting GPU leaks. Read-only window debug hook.
        ;(window as any).__r3f = { gl, scene }

        // WebGL context-lost recovery — when Chrome's GPU process is
        // under memory pressure it selectively kills the WebGL context
        // on this canvas. Without this handler the React tree tears
        // down and the entire page goes blank ("Oh nein! Fehlercode 5").
        // With the handler we keep the page intact and ask the visitor
        // to refresh — the rest of the site (footer, nav, content) stays
        // usable.
        const canvas = gl.domElement
        canvas.addEventListener('webglcontextlost', (e) => {
          e.preventDefault()
          console.warn('[Scene] WebGL context lost — GPU memory pressure likely')
          // Toggle a global flag the rest of the app can react to
          document.documentElement.dataset.webglLost = 'true'
        })
        canvas.addEventListener('webglcontextrestored', () => {
          console.info('[Scene] WebGL context restored')
          delete document.documentElement.dataset.webglLost
        })
      }}
    >
      {/* Camera mit Animation */}
      <CameraController
        viewMode={viewMode}
        targetY={cameraY}
        bbox={currentBBox}
        onTransitionStart={onTransitionStart}
        onTransitionComplete={onTransitionComplete}
      />

      {/* Scene Lighting */}
      <Lighting />

      {/* Alle Skulpturen vertikal gestapelt. Die früher hier gerenderten
          GlowShape-Geometrien sind raus — Anna möchte die Figur ohne
          aufgedrückte Polygon-Ringe atmen lassen. */}
      {artworks.map((artwork, index) => (
        <SculptureMount
          key={artwork.id}
          artwork={artwork}
          posY={-index * SCULPTURE_SPACING}
          onBBoxCalculated={handleBBoxCalculated}
        />
      ))}

      {/* Post-processing: Bloom für Neon Glow Effect. mipmapBlur=true
          and full resolution for the smooth halo around silhouettes. */}
      <EffectComposer>
        <Bloom
          intensity={settings.bloomIntensity}
          luminanceThreshold={settings.bloomThreshold}
          luminanceSmoothing={settings.bloomSmoothing}
          mipmapBlur={true}
          radius={settings.bloomRadius}
        />
      </EffectComposer>
    </Canvas>
  )
}
